import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { serverAPI } from '../../services/api';
import DashboardLayout from '../../components/layout/DashboardLayout';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import { toast } from 'react-hot-toast';
import './ServersListPage.css';

interface ServerMetrics {
  cpu_usage_percent: number;
  memory_usage_percent: number;
  disk_metrics?: Array<{
    mount: string;
    percent: number;
  }>;
}

interface Server {
  id: number;
  server_id: string;
  asset_id: number;
  asset_name?: string;
  name: string;
  server_type: 'virtual' | 'physical';
  os: string;
  os_version?: string;
  public_ip?: string;
  private_ip?: string;
  hostname?: string;
  web_server_type?: string;
  app_root_path?: string;
  status: 'online' | 'offline' | 'degraded' | 'unknown';
  last_heartbeat_at?: string;
  agent_version?: string;
  current_metrics?: ServerMetrics;
  created_at: string;
}

interface EditForm {
  name: string;
  server_type: 'virtual' | 'physical';
  os: string;
  os_version: string;
  hostname: string;
  public_ip: string;
  private_ip: string;
  web_server_type: string;
  app_root_path: string;
}

const ServersListPage: React.FC = () => {
  useDocumentTitle('Servers');
  const navigate = useNavigate();
  const { currentAsset } = useAuth();
  const [servers, setServers] = useState<Server[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Edit modal
  const [editingServer, setEditingServer] = useState<Server | null>(null);
  const [editForm, setEditForm] = useState<EditForm>({
    name: '', server_type: 'virtual', os: 'linux',
    os_version: '', hostname: '', public_ip: '', private_ip: '',
    web_server_type: '', app_root_path: '',
  });
  const [editLoading, setEditLoading] = useState(false);

  // Delete modal
  const [deletingServer, setDeletingServer] = useState<Server | null>(null);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Dropdown state
  const [openDropdown, setOpenDropdown] = useState<number | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    loadServers();
    const interval = setInterval(loadServers, 30000);
    return () => clearInterval(interval);
  }, [currentAsset]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadServers = async () => {
    try {
      const filters: { status?: string; asset_id?: number } = {};
      if (statusFilter !== 'all') filters.status = statusFilter;
      if (currentAsset) filters.asset_id = currentAsset.id;

      const response = await serverAPI.list(filters);
      if (response.data.status === 'success') {
        setServers(response.data.data.servers || []);
      }
    } catch (error: any) {
      console.error('Failed to load servers:', error);
      if (error.response?.status !== 401) toast.error('Failed to load servers');
    } finally {
      setLoading(false);
    }
  };

  // ── Edit helpers ──────────────────────────────────────────────────────────
  const openEdit = (server: Server) => {
    setOpenDropdown(null);
    setEditingServer(server);
    setEditForm({
      name: server.name,
      server_type: server.server_type,
      os: server.os,
      os_version: server.os_version || '',
      hostname: server.hostname || '',
      public_ip: server.public_ip || '',
      private_ip: server.private_ip || '',
      web_server_type: server.web_server_type || '',
      app_root_path: server.app_root_path || '',
    });
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingServer) return;
    if (!editForm.name.trim()) { toast.error('Server name is required'); return; }

    setEditLoading(true);
    try {
      await serverAPI.update(editingServer.id, editForm);
      toast.success('Server updated successfully');
      setEditingServer(null);
      loadServers();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update server');
    } finally {
      setEditLoading(false);
    }
  };

  // ── Delete helpers ────────────────────────────────────────────────────────
  const openDelete = (server: Server) => {
    setOpenDropdown(null);
    setDeletingServer(server);
    setDeleteConfirmName('');
  };

  const handleDelete = async () => {
    if (!deletingServer) return;
    if (deleteConfirmName !== deletingServer.name) {
      toast.error('Server name does not match');
      return;
    }
    setDeleteLoading(true);
    try {
      await serverAPI.delete(deletingServer.id);
      toast.success('Server deleted successfully');
      setDeletingServer(null);
      setServers(prev => prev.filter(s => s.id !== deletingServer.id));
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete server');
    } finally {
      setDeleteLoading(false);
    }
  };

  // ── Display helpers ───────────────────────────────────────────────────────
  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'online':   return { color: 'success', label: 'Online',   icon: 'bi-check-circle-fill',       pulse: true  };
      case 'offline':  return { color: 'danger',  label: 'Offline',  icon: 'bi-x-circle-fill',           pulse: false };
      case 'degraded': return { color: 'warning', label: 'Degraded', icon: 'bi-exclamation-triangle-fill',pulse: true  };
      default:         return { color: 'secondary',label: 'Unknown', icon: 'bi-question-circle-fill',    pulse: false };
    }
  };

  const getOsIcon = (os: string) => {
    switch (os?.toLowerCase()) {
      case 'linux':   return 'bi-ubuntu';
      case 'windows': return 'bi-windows';
      case 'macos':   return 'bi-apple';
      default:        return 'bi-server';
    }
  };

  const formatLastSeen = (timestamp?: string) => {
    if (!timestamp) return 'Never';
    const diff = Math.floor((Date.now() - new Date(timestamp).getTime()) / 1000);
    if (diff < 60)    return 'Just now';
    if (diff < 3600)  return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  const getProgressBarColor = (value: number) => {
    if (value >= 90) return 'danger';
    if (value >= 70) return 'warning';
    return 'success';
  };

  const filteredServers = servers.filter(s => statusFilter === 'all' || s.status === statusFilter);
  const statusCounts = {
    all: servers.length,
    online: servers.filter(s => s.status === 'online').length,
    offline: servers.filter(s => s.status === 'offline').length,
    degraded: servers.filter(s => s.status === 'degraded').length,
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="container-fluid">
        {/* Header */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
              <div>
                <h2 className="h3 mb-1">
                  <i className="bi bi-server me-2 text-primary"></i>
                  Server Monitoring
                </h2>
                <p className="text-muted mb-0">Monitor your servers in real-time with CloudAyn Agent</p>
              </div>
              <button
                className="btn btn-primary d-flex align-items-center gap-2"
                onClick={() => navigate('/servers/register')}
              >
                <i className="bi bi-plus-circle"></i>
                Register Server
              </button>
            </div>
          </div>
        </div>

        {/* Status Summary Cards */}
        <div className="row g-3 mb-4">
          {(['all', 'online', 'offline', 'degraded'] as const).map((s) => (
            <div key={s} className="col-6 col-md-3">
              <div
                className={`card border-0 shadow-sm h-100 ${statusFilter === s ? `border-${s === 'all' ? 'primary' : s === 'online' ? 'success' : s === 'offline' ? 'danger' : 'warning'} border-2` : ''}`}
                onClick={() => setStatusFilter(s)}
                style={{ cursor: 'pointer' }}
              >
                <div className="card-body text-center py-3">
                  <div className={`h2 mb-1 text-${s === 'all' ? 'primary' : s === 'online' ? 'success' : s === 'offline' ? 'danger' : 'warning'}`}>
                    {statusCounts[s]}
                  </div>
                  <small className="text-muted">{s === 'all' ? 'Total Servers' : s.charAt(0).toUpperCase() + s.slice(1)}</small>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Server Cards Grid */}
        {filteredServers.length === 0 ? (
          <div className="card border-0 shadow-sm">
            <div className="card-body text-center py-5">
              <i className="bi bi-server display-1 text-muted mb-3"></i>
              <h4>No Servers Found</h4>
              <p className="text-muted mb-4">
                {servers.length === 0
                  ? "You haven't registered any servers yet. Register a server to start monitoring."
                  : "No servers match the selected filter."}
              </p>
              {servers.length === 0 && (
                <button className="btn btn-primary" onClick={() => navigate('/servers/register')}>
                  <i className="bi bi-plus-circle me-2"></i>
                  Register Your First Server
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="row g-4">
            {filteredServers.map((server) => {
              const statusConfig = getStatusConfig(server.status);
              const cpuUsage    = server.current_metrics?.cpu_usage_percent || 0;
              const memoryUsage = server.current_metrics?.memory_usage_percent || 0;
              const diskUsage   = server.current_metrics?.disk_metrics?.[0]?.percent || 0;

              return (
                <div key={server.id} className="col-12 col-md-6 col-xl-4">
                  <div className="card border-0 shadow-sm h-100 server-card">
                    <div className="card-body">
                      {/* Header */}
                      <div className="d-flex align-items-start justify-content-between mb-3">
                        <div className="d-flex align-items-center gap-3">
                          <div className={`server-icon bg-${statusConfig.color} bg-opacity-10`}>
                            <i className={`bi ${getOsIcon(server.os)} text-${statusConfig.color}`}></i>
                          </div>
                          <div>
                            <h5 className="mb-0 fw-semibold">{server.name}</h5>
                            <small className="text-muted">
                              {server.os}{server.os_version ? ` · ${server.os_version}` : ''}
                            </small>
                          </div>
                        </div>

                        <div className="d-flex align-items-center gap-2">
                          <div className={`status-indicator ${statusConfig.pulse ? 'pulse' : ''}`}>
                            <span className={`badge bg-${statusConfig.color} rounded-pill d-flex align-items-center gap-1`}>
                              <i className={`bi ${statusConfig.icon}`}></i>
                              {statusConfig.label}
                            </span>
                          </div>

                          {/* Three-dot menu */}
                          <div className="position-relative" ref={openDropdown === server.id ? dropdownRef : null}>
                            <button
                              className="btn btn-sm btn-outline-secondary border-0 p-1"
                              style={{ lineHeight: 1 }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenDropdown(openDropdown === server.id ? null : server.id);
                              }}
                              title="Options"
                            >
                              <i className="bi bi-three-dots-vertical"></i>
                            </button>
                            {openDropdown === server.id && (
                              <div
                                className="dropdown-menu show shadow"
                                style={{ right: 0, left: 'auto', minWidth: '130px', zIndex: 1050 }}
                              >
                                <button
                                  className="dropdown-item d-flex align-items-center gap-2"
                                  onClick={() => openEdit(server)}
                                >
                                  <i className="bi bi-pencil text-primary"></i>
                                  Edit
                                </button>
                                <div className="dropdown-divider"></div>
                                <button
                                  className="dropdown-item d-flex align-items-center gap-2 text-danger"
                                  onClick={() => openDelete(server)}
                                >
                                  <i className="bi bi-trash3"></i>
                                  Delete
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Server Info */}
                      <div className="server-info mb-3">
                        <div className="row g-2 text-muted small">
                          {server.hostname && (
                            <div className="col-12">
                              <i className="bi bi-hdd-network me-1"></i>{server.hostname}
                            </div>
                          )}
                          {server.public_ip && (
                            <div className="col-6">
                              <i className="bi bi-globe me-1"></i>{server.public_ip}
                            </div>
                          )}
                          {server.web_server_type && (
                            <div className="col-6">
                              <i className="bi bi-box me-1"></i>{server.web_server_type}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Metrics Progress Bars */}
                      {server.status !== 'unknown' && (
                        <div className="metrics-section mb-3">
                          {[
                            { label: 'CPU',     icon: 'bi-cpu',    value: cpuUsage    },
                            { label: 'Memory',  icon: 'bi-memory', value: memoryUsage },
                            { label: 'Storage', icon: 'bi-hdd',    value: diskUsage   },
                          ].map(({ label, icon, value }) => (
                            <div key={label} className="metric-item mb-2">
                              <div className="d-flex justify-content-between align-items-center mb-1">
                                <small className="text-muted"><i className={`bi ${icon} me-1`}></i>{label}</small>
                                <small className={`fw-semibold text-${getProgressBarColor(value)}`}>{value.toFixed(1)}%</small>
                              </div>
                              <div className="progress" style={{ height: '6px' }}>
                                <div className={`progress-bar bg-${getProgressBarColor(value)}`} style={{ width: `${value}%` }}></div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Footer */}
                      <div className="d-flex justify-content-between align-items-center mt-auto pt-3 border-top">
                        <small className="text-muted">
                          <i className="bi bi-clock me-1"></i>
                          {formatLastSeen(server.last_heartbeat_at)}
                        </small>
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => navigate(`/servers/${server.id}`)}
                        >
                          <i className="bi bi-eye me-1"></i>View Details
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Edit Server Modal ──────────────────────────────────────────────── */}
      {editingServer && (
        <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.5)', zIndex: 1055 }} onClick={() => !editLoading && setEditingServer(null)}>
          <div className="modal-dialog modal-lg modal-dialog-centered" onClick={e => e.stopPropagation()}>
            <div className="modal-content border-0 shadow">
              <div className="modal-header border-0 pb-0">
                <h5 className="modal-title fw-semibold">
                  <i className="bi bi-pencil-square text-primary me-2"></i>
                  Edit Server
                </h5>
                <button type="button" className="btn-close" onClick={() => setEditingServer(null)} disabled={editLoading}></button>
              </div>
              <form onSubmit={handleEditSubmit}>
                <div className="modal-body pt-3">
                  <div className="row g-3">
                    <div className="col-12">
                      <label className="form-label fw-semibold">Server Name <span className="text-danger">*</span></label>
                      <input
                        type="text"
                        className="form-control"
                        value={editForm.name}
                        onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))}
                        placeholder="My Production Server"
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">Server Type</label>
                      <select
                        className="form-select"
                        value={editForm.server_type}
                        onChange={e => setEditForm(f => ({ ...f, server_type: e.target.value as 'virtual' | 'physical' }))}
                      >
                        <option value="virtual">Virtual</option>
                        <option value="physical">Physical</option>
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">Operating System</label>
                      <select
                        className="form-select"
                        value={editForm.os}
                        onChange={e => setEditForm(f => ({ ...f, os: e.target.value }))}
                      >
                        <option value="linux">Linux</option>
                        <option value="windows">Windows</option>
                        <option value="macos">macOS</option>
                        <option value="freebsd">FreeBSD</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">OS Version</label>
                      <input
                        type="text"
                        className="form-control"
                        value={editForm.os_version}
                        onChange={e => setEditForm(f => ({ ...f, os_version: e.target.value }))}
                        placeholder="e.g. Ubuntu 22.04"
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">Hostname</label>
                      <input
                        type="text"
                        className="form-control"
                        value={editForm.hostname}
                        onChange={e => setEditForm(f => ({ ...f, hostname: e.target.value }))}
                        placeholder="server.example.com"
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">Public IP</label>
                      <input
                        type="text"
                        className="form-control"
                        value={editForm.public_ip}
                        onChange={e => setEditForm(f => ({ ...f, public_ip: e.target.value }))}
                        placeholder="203.0.113.1"
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">Private IP</label>
                      <input
                        type="text"
                        className="form-control"
                        value={editForm.private_ip}
                        onChange={e => setEditForm(f => ({ ...f, private_ip: e.target.value }))}
                        placeholder="10.0.0.1"
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">Web Server</label>
                      <select
                        className="form-select"
                        value={editForm.web_server_type}
                        onChange={e => setEditForm(f => ({ ...f, web_server_type: e.target.value }))}
                      >
                        <option value="">None / Unknown</option>
                        <option value="nginx">Nginx</option>
                        <option value="apache">Apache</option>
                        <option value="nodejs">Node.js</option>
                        <option value="tomcat">Tomcat</option>
                        <option value="caddy">Caddy</option>
                        <option value="lighttpd">Lighttpd</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">App Root Path</label>
                      <input
                        type="text"
                        className="form-control"
                        value={editForm.app_root_path}
                        onChange={e => setEditForm(f => ({ ...f, app_root_path: e.target.value }))}
                        placeholder="/var/www/html"
                      />
                    </div>
                  </div>
                </div>
                <div className="modal-footer border-0 pt-0">
                  <button type="button" className="btn btn-outline-secondary" onClick={() => setEditingServer(null)} disabled={editLoading}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={editLoading}>
                    {editLoading ? <><span className="spinner-border spinner-border-sm me-2"></span>Saving...</> : <><i className="bi bi-check-lg me-1"></i>Save Changes</>}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Confirmation Modal ──────────────────────────────────────── */}
      {deletingServer && (
        <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.5)', zIndex: 1055 }} onClick={() => !deleteLoading && setDeletingServer(null)}>
          <div className="modal-dialog modal-dialog-centered" onClick={e => e.stopPropagation()}>
            <div className="modal-content border-0 shadow">
              <div className="modal-header border-0 pb-0">
                <h5 className="modal-title fw-semibold text-danger">
                  <i className="bi bi-trash3 me-2"></i>Delete Server
                </h5>
                <button type="button" className="btn-close" onClick={() => setDeletingServer(null)} disabled={deleteLoading}></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-danger d-flex align-items-start gap-2 mb-4">
                  <i className="bi bi-exclamation-triangle-fill mt-1 flex-shrink-0"></i>
                  <div>
                    <strong>This action cannot be undone.</strong>
                    <div className="small mt-1">
                      The server will be removed from monitoring. Historical metrics and events will be retained.
                      Make sure to uninstall the agent from the server to stop it reporting.
                    </div>
                  </div>
                </div>

                <div className="mb-3">
                  <p className="text-muted small mb-1">
                    To confirm, type the server name:
                  </p>
                  <p className="fw-semibold mb-3" style={{ userSelect: 'none' }}>
                    <code>{deletingServer.name}</code>
                  </p>
                  <input
                    type="text"
                    className={`form-control ${deleteConfirmName && deleteConfirmName !== deletingServer.name ? 'is-invalid' : deleteConfirmName === deletingServer.name ? 'is-valid' : ''}`}
                    placeholder={`Type "${deletingServer.name}" to confirm`}
                    value={deleteConfirmName}
                    onChange={e => setDeleteConfirmName(e.target.value)}
                    autoFocus
                    disabled={deleteLoading}
                  />
                  {deleteConfirmName && deleteConfirmName !== deletingServer.name && (
                    <div className="invalid-feedback">Server name does not match.</div>
                  )}
                </div>
              </div>
              <div className="modal-footer border-0 pt-0">
                <button type="button" className="btn btn-outline-secondary" onClick={() => setDeletingServer(null)} disabled={deleteLoading}>
                  Cancel
                </button>
                <button
                  className="btn btn-danger"
                  onClick={handleDelete}
                  disabled={deleteConfirmName !== deletingServer.name || deleteLoading}
                >
                  {deleteLoading
                    ? <><span className="spinner-border spinner-border-sm me-2"></span>Deleting...</>
                    : <><i className="bi bi-trash3 me-1"></i>Delete Server</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default ServersListPage;
